import React, { useState, useEffect } from "react";
import {
  User,
  Phone,
  Mail,
  MapPin,
  Users,
  MessageCircle,
  Send,
  CheckCircle2,
  Sparkles,
} from "lucide-react";
import matajerLogo from "../assets/matajer-logo.png";
import Swal from "sweetalert2";
import saudiLogo from "../assets/businessLogo.png";
import { FaWhatsapp } from "react-icons/fa";

import { collection, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";

function getReferralCode() {
  const params = new URLSearchParams(window.location.search);
  return params.get("ref") || "";
}

const initialForm = {
  firstName: "",
  lastName: "",
  phone: "",
  email: "",
  city: "",
  roles: [],
  otherRole: "",
  message: "",
};

export default function SurveyForm() {
  const [form, setForm] = useState(initialForm);
  const [loading, setLoading] = useState(false);
  const [referral, setReferral] = useState("");
  const [focusedField, setFocusedField] = useState("");
  const [showContactIcons, setShowContactIcons] = useState(false);
  const contactRef = React.useRef();
  const [phoneError, setPhoneError] = useState("");
  const phoneRef = React.useRef(null);


  useEffect(() => {
    function handleClickOutside(event) {
      if (contactRef.current && !contactRef.current.contains(event.target)) {
        setShowContactIcons(false);
      }
    }
    if (showContactIcons) {
      document.addEventListener("mousedown", handleClickOutside);
    } else {
      document.removeEventListener("mousedown", handleClickOutside);
    }
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showContactIcons]);

  useEffect(() => {
    setReferral(getReferralCode());
  }, []);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    if (type === "checkbox") {
      setForm((prev) => {
        if (checked) {
          return { ...prev, roles: [...prev.roles, value] };
        } else {
          return { ...prev, roles: prev.roles.filter((r) => r !== value) };
        }
      });
    } else {
      setForm((prev) => ({ ...prev, [name]: value }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // تحقق إضافي لرقم الجوال
    if (!form.firstName.trim() || !form.phone.trim() || !form.city.trim()) {
      await Swal.fire({
        icon: "error",
        title: "يرجى تعبئة جميع الحقول الإلزامية",
        text: "الاسم الأول ورقم الجوال والمدينة مطلوبة",
        confirmButtonColor: "#89BCDB",
        confirmButtonText: "إغلاق",
      });
      return;
    }

    if (!/^05\d{8}$/.test(form.phone)) {
      setPhoneError("رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام فقط");
      phoneRef.current?.focus();
      return;
    }

    setPhoneError(""); // يمسح أي رسالة خطأ لو كل شيء صح
    setLoading(true);

    try {
      await addDoc(collection(db, "survey_form"), {
        ...form,
        referral,
        createdAt: serverTimestamp(),
      });

      await Swal.fire({
        icon: "success",
        title: "تم التسجيل بنجاح!",
        html: `
        <h3>شكراً لاهتمامك بالتعاون معنا! 🤝</h3>
        <p>تم استلام معلوماتك وسيتواصل معك الفريق المختص قريباً لمناقشة فرص التعاون المتاحة.<br>نقدر مبادرتك ونتطلع للعمل معاً 🌟</p>
      `,
        confirmButtonColor: "#89BCDB",
        confirmButtonText: "إغلاق",
      });

      setForm(initialForm);
    } catch (err) {
      await Swal.fire({
        icon: "error",
        title: "حدث خطأ أثناء إرسال البيانات!",
        text: "يرجى المحاولة مرة أخرى لاحقاً.",
        confirmButtonColor: "#89BCDB",
        confirmButtonText: "إغلاق",
      });
    } finally {
      setLoading(false);
    }
  };
  
  return (
    <div
      style={{
        minHeight: "100vh",
        background: "linear-gradient(135deg, #7EBFE9 0%, #EA9B56 100%)",
        fontFamily: "'Tajawal', sans-serif",
        padding: "0",
      }}
      dir="rtl"
    >
      {/* Background Decorations */}
      <div
        style={{
          position: "absolute",
          width: "100%",
          height: "100%",
          overflow: "hidden",
          zIndex: 0,
        }}
      >
        <div
          style={{
            position: "absolute",
            top: "10%",
            right: "5%",
            width: "200px",
            height: "200px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.1)",
            filter: "blur(40px)",
          }}
        ></div>
        <div
          style={{
            position: "absolute",
            bottom: "20%",
            left: "8%",
            width: "150px",
            height: "150px",
            borderRadius: "50%",
            background: "rgba(255,255,255,0.08)",
            filter: "blur(30px)",
          }}
        ></div>
      </div>

      <div
        className="container-fluid"
        style={{ position: "relative", zIndex: 1, padding: "0" }}
      >
        {/* Header Section */}
        <div
          style={{
            background: "rgba(255,255,255,0.1)",
            backdropFilter: "blur(20px)",
            padding: "2rem 0",
            marginBottom: "1.3rem",
            borderBottom: "1px solid rgba(255,255,255,0.2)",
          }}
        >
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12 text-center">
                {/* Logo */}
                <div
                  style={{
                    background: "white",
                    borderRadius: "25px",
                    padding: "1.5rem 2rem",
                    display: "inline-block",
                    boxShadow: "0 25px 60px rgba(0,0,0,0.15)",
                    // marginBottom: "0.1rem",
                    transform: "scale(1)",
                    transition: "all 0.4s ease",
                  }}
                >
                  <img
                    src={matajerLogo}
                    alt="شعار متاجر الحي"
                    style={{
                      maxWidth: "200px",
                      height: "auto",
                      display: "block",
                    }}
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Main Form Section */}
        <div className="container">
          <div className="row justify-content-center">
            <div className="col-12 col-lg-10 col-xl-9">
              {/* Form Card */}
              <div
                style={{
                  background: "rgba(255,255,255,0.98)",
                  backdropFilter: "blur(20px)",
                  borderRadius: "30px",
                  boxShadow: "0 30px 80px rgba(0,0,0,0.15)",
                  border: "1px solid rgba(255,255,255,0.3)",
                  overflow: "hidden",
                  marginBottom: "3rem",
                }}
              >
                {/* Form Header */}
                <div
                  style={{
                    background:
                      "linear-gradient(135deg, rgba(126,191,233,0.1) 0%, rgba(234,155,86,0.1) 100%)",
                    padding: "2.5rem 3rem 2rem",
                    textAlign: "center",
                    borderBottom: "1px solid rgba(0,0,0,0.05)",
                  }}
                >
                  <div
                    style={{
                      display: "inline-flex",
                      alignItems: "center",
                      background:
                        "linear-gradient(135deg, #7EBFE9 0%, #EA9B56 100%)",
                      color: "white",
                      padding: "1rem 2rem",
                      borderRadius: "50px",
                      fontSize: "1.3rem",
                      fontWeight: "bold",
                      marginBottom: "1rem",
                      boxShadow: "0 10px 30px rgba(126,191,233,0.3)",
                    }}
                  >
                    <Sparkles size={24} style={{ marginLeft: "0.5rem" }} />
                    📝 تسجيل عضو متاجر الحي
                  </div>

                  <p
                    style={{
                      color: "#666",
                      fontSize: "1.1rem",
                      margin: "0",
                      fontWeight: "500",
                    }}
                  >
                    كن من أوائل أعضاء "متاجر الحي"
                  </p>
                  <div
                    style={{
                      color: "#666",
                      fontSize: "1.1rem",
                      fontWeight: "500",
                      marginTop: "0.9rem",
                      marginBottom: "0.3rem",
                      textAlign: "right",
                      lineHeight: "2",
                    }}
                  >
                    برنامج يكافئك على مساهمتك في تطوير منصة حيك
                    <br />
                    ✅ التسجيل مجاني وغير ملزم
                    <br />
                    ✅ ستصلك التفاصيل الكاملة قريباً
                    <br />✅ مكافآت وكوبونات حقيقية من متاجر حيك
                  </div>
                </div>

                {/* Form Body */}
                <div style={{ padding: "3rem" }}>
                  {/* Personal Information Section */}
                  <div style={{ marginBottom: "3rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "2rem",
                        paddingBottom: "1rem",
                        borderBottom: "2px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #7EBFE9 0%, #60a5fa 100%)",
                          borderRadius: "15px",
                          padding: "0.8rem",
                          marginLeft: "1rem",
                        }}
                      >
                        <User size={22} style={{ color: "white" }} />
                      </div>
                      <h3
                        style={{
                          fontSize: "1.4rem",
                          fontWeight: "bold",
                          color: "#333",
                          margin: "0",
                          fontFamily: "'Tajawal', sans-serif",
                        }}
                      >
                        المعلومات الشخصية
                      </h3>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-4">
                        <label
                          style={{
                            display: "block",
                            fontWeight: "600",
                            color: "#444",
                            marginBottom: "0.8rem",
                            fontSize: "1.1rem",
                            fontFamily: "'Tajawal', sans-serif",
                          }}
                        >
                          الاسم الأول{" "}
                          <span style={{ color: "#e74c3c" }}>*</span>
                        </label>
                        <div style={{ position: "relative" }}>
                          <input
                            name="firstName"
                            value={form.firstName}
                            onChange={handleChange}
                            onFocus={() => setFocusedField("firstName")}
                            onBlur={() => setFocusedField("")}
                            required
                            placeholder="اكتب اسمك الأول"
                            disabled={loading}
                            style={{
                              width: "100%",
                              padding: "1.2rem 2.5rem 1.2rem 3.5rem",
                              border:
                                focusedField === "firstName"
                                  ? "3px solid #7EBFE9"
                                  : "2px solid #e0e0e0",
                              borderRadius: "15px",
                              fontSize: "1.1rem",
                              transition: "all 0.3s ease",
                              outline: "none",
                              fontFamily: "'Tajawal', sans-serif",
                              backgroundColor:
                                focusedField === "firstName"
                                  ? "#f8fdff"
                                  : "white",
                              transform:
                                focusedField === "firstName"
                                  ? "translateY(-2px)"
                                  : "none",
                              boxShadow:
                                focusedField === "firstName"
                                  ? "0 8px 25px rgba(126,191,233,0.15)"
                                  : "0 2px 10px rgba(0,0,0,0.05)",
                            }}
                          />
                          <User
                            size={20}
                            style={{
                              position: "absolute",
                              right: "1.2rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color:
                                focusedField === "firstName"
                                  ? "#7EBFE9"
                                  : "#999",
                              transition: "color 0.3s ease",
                            }}
                          />
                        </div>
                      </div>

                      <div className="col-md-6 mb-4">
                        <label
                          style={{
                            display: "block",
                            fontWeight: "600",
                            color: "#444",
                            marginBottom: "0.8rem",
                            fontSize: "1.1rem",
                            fontFamily: "'Tajawal', sans-serif",
                          }}
                        >
                          باقي الاسم
                        </label>
                        <div style={{ position: "relative" }}>
                          <input
                            name="lastName"
                            value={form.lastName}
                            onChange={handleChange}
                            onFocus={() => setFocusedField("lastName")}
                            onBlur={() => setFocusedField("")}
                            placeholder="باقي اسمك (اختياري)"
                            disabled={loading}
                            style={{
                              width: "100%",
                              padding: "1.2rem 2.5rem 1.2rem 3.5rem",
                              border:
                                focusedField === "lastName"
                                  ? "3px solid #7EBFE9"
                                  : "2px solid #e0e0e0",
                              borderRadius: "15px",
                              fontSize: "1.1rem",
                              transition: "all 0.3s ease",
                              outline: "none",
                              fontFamily: "'Tajawal', sans-serif",
                              backgroundColor:
                                focusedField === "lastName"
                                  ? "#f8fdff"
                                  : "white",
                              transform:
                                focusedField === "lastName"
                                  ? "translateY(-2px)"
                                  : "none",
                              boxShadow:
                                focusedField === "lastName"
                                  ? "0 8px 25px rgba(126,191,233,0.15)"
                                  : "0 2px 10px rgba(0,0,0,0.05)",
                            }}
                          />
                          <User
                            size={20}
                            style={{
                              position: "absolute",
                              right: "1.2rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color:
                                focusedField === "lastName"
                                  ? "#7EBFE9"
                                  : "#999",
                              transition: "color 0.3s ease",
                            }}
                          />
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Contact Information Section */}
                  <div style={{ marginBottom: "3rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "2rem",
                        paddingBottom: "1rem",
                        borderBottom: "2px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #EA9B56 0%, #f97316 100%)",
                          borderRadius: "15px",
                          padding: "0.8rem",
                          marginLeft: "1rem",
                        }}
                      >
                        <Phone size={22} style={{ color: "white" }} />
                      </div>
                      <h3
                        style={{
                          fontSize: "1.4rem",
                          fontWeight: "bold",
                          color: "#333",
                          margin: "0",
                          fontFamily: "'Tajawal', sans-serif",
                        }}
                      >
                        معلومات التواصل
                      </h3>
                    </div>

                    <div className="row">
                      <div className="col-md-6 mb-4">
                        <label
                          style={{
                            display: "block",
                            fontWeight: "600",
                            color: "#444",
                            marginBottom: "0.8rem",
                            fontSize: "1.1rem",
                            fontFamily: "'Tajawal', sans-serif",
                          }}
                        >
                          رقم الجوال (واتساب){" "}
                          <span style={{ color: "#e74c3c" }}>*</span>
                        </label>

                        <div style={{ position: "relative" }}>
                          <input
                            ref={phoneRef}
                            name="phone"
                            value={form.phone}
                            onChange={(e) => {
                              // أرقام فقط وبحد أقصى 10
                              const val = e.target.value
                                .replace(/\D/g, "")
                                .slice(0, 10);
                              setForm((prev) => ({ ...prev, phone: val }));
                              if (phoneError) setPhoneError("");
                            }}
                            onFocus={() => setFocusedField("phone")}
                            onBlur={() => {
                              setFocusedField("");
                              if (!/^05\d{8}$/.test(form.phone)) {
                                setPhoneError(
                                  "رقم الجوال يجب أن يبدأ بـ 05 ويتكون من 10 أرقام فقط"
                                );
                              } else {
                                setPhoneError("");
                              }
                            }}
                            required
                            placeholder="مثال: 05xxxxxxxx"
                            disabled={loading}
                            type="tel"
                            inputMode="numeric"
                            pattern="^05\\d{8}$"
                            maxLength={10}
                            aria-describedby={
                              phoneError ? "phone-error" : undefined
                            }
                            style={{
                              width: "100%",
                              padding: "1.2rem 2.5rem 1.2rem 3.5rem",
                              border:
                                focusedField === "phone"
                                  ? "3px solid #EA9B56"
                                  : "2px solid #e0e0e0",
                              borderRadius: "15px",
                              fontSize: "1.1rem",
                              transition: "all 0.3s ease",
                              outline: "none",
                              fontFamily: "'Tajawal', sans-serif",
                              backgroundColor:
                                focusedField === "phone" ? "#fffaf7" : "white",
                              transform:
                                focusedField === "phone"
                                  ? "translateY(-2px)"
                                  : "none",
                              boxShadow:
                                focusedField === "phone"
                                  ? "0 8px 25px rgba(234,155,86,0.15)"
                                  : "0 2px 10px rgba(0,0,0,0.05)",
                            }}
                          />

                          <Phone
                            size={20}
                            style={{
                              position: "absolute",
                              right: "1.2rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color:
                                focusedField === "phone" ? "#EA9B56" : "#999",
                              transition: "color 0.3s ease",
                            }}
                          />

                          {phoneError && (
                            <div
                              id="phone-error"
                              role="alert"
                              style={{
                                color: "#ea9b56",
                                fontSize: "0.98rem",
                                marginTop: "0.3rem",
                                fontWeight: "500",
                                background: "rgba(234,155,86,0.07)",
                                padding: "0.38rem 0.9rem",
                                borderRadius: "9px",
                                textAlign: "right",
                                transition: "all 0.2s",
                                minHeight: "20px",
                              }}
                            >
                              {phoneError}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="col-md-6 mb-4">
                        <label
                          style={{
                            display: "block",
                            fontWeight: "600",
                            color: "#444",
                            marginBottom: "0.8rem",
                            fontSize: "1.1rem",
                            fontFamily: "'Tajawal', sans-serif",
                          }}
                        >
                          البريد الإلكتروني
                        </label>
                        <div style={{ position: "relative" }}>
                          <input
                            name="email"
                            type="email"
                            value={form.email}
                            onChange={handleChange}
                            onFocus={() => setFocusedField("email")}
                            onBlur={() => setFocusedField("")}
                            placeholder="اختياري"
                            disabled={loading}
                            style={{
                              width: "100%",
                              padding: "1.2rem 2.5rem 1.2rem 3.5rem",
                              border:
                                focusedField === "email"
                                  ? "3px solid #EA9B56"
                                  : "2px solid #e0e0e0",
                              borderRadius: "15px",
                              fontSize: "1.1rem",
                              transition: "all 0.3s ease",
                              outline: "none",
                              fontFamily: "'Tajawal', sans-serif",
                              backgroundColor:
                                focusedField === "email" ? "#fffaf7" : "white",
                              transform:
                                focusedField === "email"
                                  ? "translateY(-2px)"
                                  : "none",
                              boxShadow:
                                focusedField === "email"
                                  ? "0 8px 25px rgba(234,155,86,0.15)"
                                  : "0 2px 10px rgba(0,0,0,0.05)",
                            }}
                          />
                          <Mail
                            size={20}
                            style={{
                              position: "absolute",
                              right: "1.2rem",
                              top: "50%",
                              transform: "translateY(-50%)",
                              color:
                                focusedField === "email" ? "#EA9B56" : "#999",
                              transition: "color 0.3s ease",
                            }}
                          />
                        </div>
                      </div>
                    </div>

                    <div className="mb-4">
                      <label
                        style={{
                          display: "block",
                          fontWeight: "600",
                          color: "#444",
                          marginBottom: "0.8rem",
                          fontSize: "1.1rem",
                          fontFamily: "'Tajawal', sans-serif",
                        }}
                      >
                        المدينة/الحي <span style={{ color: "#e74c3c" }}>*</span>
                      </label>
                      <div style={{ position: "relative" }}>
                        <input
                          name="city"
                          value={form.city}
                          onChange={handleChange}
                          onFocus={() => setFocusedField("city")}
                          onBlur={() => setFocusedField("")}
                          required
                          placeholder="اسم المدينة أو الحي"
                          disabled={loading}
                          style={{
                            width: "100%",
                            padding: "1.2rem 2.5rem 1.2rem 3.5rem",
                            border:
                              focusedField === "city"
                                ? "3px solid #EA9B56"
                                : "2px solid #e0e0e0",
                            borderRadius: "15px",
                            fontSize: "1.1rem",
                            transition: "all 0.3s ease",
                            outline: "none",
                            fontFamily: "'Tajawal', sans-serif",
                            backgroundColor:
                              focusedField === "city" ? "#fffaf7" : "white",
                            transform:
                              focusedField === "city"
                                ? "translateY(-2px)"
                                : "none",
                            boxShadow:
                              focusedField === "city"
                                ? "0 8px 25px rgba(234,155,86,0.15)"
                                : "0 2px 10px rgba(0,0,0,0.05)",
                          }}
                        />
                        <MapPin
                          size={20}
                          style={{
                            position: "absolute",
                            right: "1.2rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            color: focusedField === "city" ? "#EA9B56" : "#999",
                            transition: "color 0.3s ease",
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Collaboration Section */}
                  <div style={{ marginBottom: "3rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "2rem",
                        paddingBottom: "1rem",
                        borderBottom: "2px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #10b981 0%, #059669 100%)",
                          borderRadius: "15px",
                          padding: "0.8rem",
                          marginLeft: "1rem",
                        }}
                      >
                        <Users size={22} style={{ color: "white" }} />
                      </div>
                      <div>
                        <h3
                          style={{
                            fontSize: "1.4rem",
                            fontWeight: "bold",
                            color: "#333",
                            margin: "0",
                            fontFamily: "'Tajawal', sans-serif",
                          }}
                        >
                          إذا حاب تتعاون أكثر ونربح بعض:
                        </h3>
                        <p
                          style={{
                            color: "#666",
                            fontSize: "1rem",
                            margin: "0.3rem 0 0 0",
                            fontWeight: "normal",
                          }}
                        >
                          (اختياري)
                        </p>
                      </div>
                    </div>

                    <div className="row">
                      {[
                        "مؤثر/مشهور محلي",
                        "ممثل جمعية خيرية",
                        "تاجر مهتم بالتعاون",
                        "صاحب فود ترك",
                        "من الأسر المنتجة",
                      ].map((role) => (
                        <div className="col-12 col-md-6 mb-3" key={role}>
                          <div
                            style={{
                              padding: "1.2rem",
                              border: form.roles.includes(role)
                                ? "3px solid #10b981"
                                : "2px solid #e0e0e0",
                              borderRadius: "15px",
                              cursor: "pointer",
                              transition: "all 0.3s ease",
                              backgroundColor: form.roles.includes(role)
                                ? "#f0fdf4"
                                : "white",
                              transform: form.roles.includes(role)
                                ? "translateY(-2px)"
                                : "none",
                              boxShadow: form.roles.includes(role)
                                ? "0 8px 25px rgba(16,185,129,0.15)"
                                : "0 2px 10px rgba(0,0,0,0.05)",
                              position: "relative",
                            }}
                            onClick={() => {
                              const event = {
                                target: {
                                  name: "roles",
                                  value: role,
                                  type: "checkbox",
                                  checked: !form.roles.includes(role),
                                },
                              };
                              handleChange(event);
                            }}
                          >
                            <div
                              style={{ display: "flex", alignItems: "center" }}
                            >
                              <input
                                type="checkbox"
                                name="roles"
                                value={role}
                                checked={form.roles.includes(role)}
                                onChange={handleChange}
                                disabled={loading}
                                id={role}
                                style={{
                                  width: "22px",
                                  height: "22px",
                                  marginLeft: "1rem",
                                  accentColor: "#10b981",
                                  cursor: "pointer",
                                }}
                              />
                              <label
                                style={{
                                  cursor: "pointer",
                                  fontFamily: "'Tajawal', sans-serif",
                                  color: "#374151",
                                  fontWeight: "500",
                                  fontSize: "1.1rem",
                                  flex: 1,
                                }}
                                htmlFor={role}
                              >
                                {role}
                              </label>
                            </div>
                            {form.roles.includes(role) && (
                              <CheckCircle2
                                size={24}
                                style={{
                                  position: "absolute",
                                  top: "1rem",
                                  left: "1rem",
                                  color: "#10b981",
                                }}
                              />
                            )}
                          </div>
                        </div>
                      ))}

                      {/* أخرى */}
                      <div className="col-12 col-md-6 mb-3">
                        <div
                          style={{
                            padding: "1.2rem",
                            border: form.roles.includes("أخرى")
                              ? "3px solid #10b981"
                              : "2px solid #e0e0e0",
                            borderRadius: "15px",
                            cursor: "pointer",
                            transition: "all 0.3s ease",
                            backgroundColor: form.roles.includes("أخرى")
                              ? "#f0fdf4"
                              : "white",
                            transform: form.roles.includes("أخرى")
                              ? "translateY(-2px)"
                              : "none",
                            boxShadow: form.roles.includes("أخرى")
                              ? "0 8px 25px rgba(16,185,129,0.15)"
                              : "0 2px 10px rgba(0,0,0,0.05)",
                            position: "relative",
                          }}
                          onClick={() => {
                            const event = {
                              target: {
                                name: "roles",
                                value: "أخرى",
                                type: "checkbox",
                                checked: !form.roles.includes("أخرى"),
                              },
                            };
                            handleChange(event);
                          }}
                        >
                          <div
                            style={{ display: "flex", alignItems: "center" }}
                          >
                            <input
                              type="checkbox"
                              name="roles"
                              value="أخرى"
                              checked={form.roles.includes("أخرى")}
                              onChange={handleChange}
                              disabled={loading}
                              id="otherRole"
                              style={{
                                width: "22px",
                                height: "22px",
                                marginLeft: "1rem",
                                accentColor: "#10b981",
                                cursor: "pointer",
                              }}
                            />
                            <label
                              style={{
                                cursor: "pointer",
                                fontFamily: "'Tajawal', sans-serif",
                                color: "#374151",
                                fontWeight: "500",
                                fontSize: "1.1rem",
                                flex: 1,
                              }}
                              htmlFor="otherRole"
                            >
                              أخرى:
                            </label>
                          </div>
                          {form.roles.includes("أخرى") && (
                            <CheckCircle2
                              size={24}
                              style={{
                                position: "absolute",
                                top: "1rem",
                                left: "1rem",
                                color: "#10b981",
                              }}
                            />
                          )}
                        </div>

                        {form.roles.includes("أخرى") && (
                          <input
                            name="otherRole"
                            value={form.otherRole}
                            onChange={handleChange}
                            onFocus={() => setFocusedField("otherRole")}
                            onBlur={() => setFocusedField("")}
                            placeholder="اذكر نوع التعاون"
                            disabled={loading}
                            style={{
                              width: "100%",
                              padding: "1rem 1.5rem",
                              border:
                                focusedField === "otherRole"
                                  ? "3px solid #10b981"
                                  : "2px solid #e0e0e0",
                              borderRadius: "12px",
                              fontSize: "1rem",
                              transition: "all 0.3s ease",
                              outline: "none",
                              fontFamily: "'Tajawal', sans-serif",
                              marginTop: "1rem",
                              backgroundColor:
                                focusedField === "otherRole"
                                  ? "#f0fdf4"
                                  : "white",
                              boxShadow:
                                focusedField === "otherRole"
                                  ? "0 5px 15px rgba(16,185,129,0.1)"
                                  : "0 2px 8px rgba(0,0,0,0.05)",
                            }}
                          />
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Message Section */}
                  <div style={{ marginBottom: "3rem" }}>
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        marginBottom: "2rem",
                        paddingBottom: "1rem",
                        borderBottom: "2px solid #f0f0f0",
                      }}
                    >
                      <div
                        style={{
                          background:
                            "linear-gradient(135deg, #8b5cf6 0%, #7c3aed 100%)",
                          borderRadius: "15px",
                          padding: "0.8rem",
                          marginLeft: "1rem",
                        }}
                      >
                        <MessageCircle size={22} style={{ color: "white" }} />
                      </div>
                      <h3
                        style={{
                          fontSize: "1.4rem",
                          fontWeight: "bold",
                          color: "#333",
                          margin: "0",
                          fontFamily: "'Tajawal', sans-serif",
                        }}
                      >
                        عندك رسالة للإدارة؟
                      </h3>
                    </div>

                    <div style={{ position: "relative" }}>
                      <textarea
                        name="message"
                        rows={3}
                        value={form.message}
                        onChange={handleChange}
                        onFocus={() => setFocusedField("message")}
                        onBlur={() => setFocusedField("")}
                        placeholder="اكتب رسالتك هنا (اختياري)"
                        disabled={loading}
                        style={{
                          width: "100%",
                          padding: "1.2rem 2.5rem 1.2rem 3.5rem",
                          border:
                            focusedField === "message"
                              ? "3px solid #8b5cf6"
                              : "2px solid #e0e0e0",
                          borderRadius: "15px",
                          fontSize: "1.1rem",
                          transition: "all 0.3s ease",
                          outline: "none",
                          resize: "vertical",
                          fontFamily: "'Tajawal', sans-serif",
                          backgroundColor:
                            focusedField === "message" ? "#faf7ff" : "white",
                          transform:
                            focusedField === "message"
                              ? "translateY(-2px)"
                              : "none",
                          boxShadow:
                            focusedField === "message"
                              ? "0 8px 25px rgba(139,92,246,0.15)"
                              : "0 2px 10px rgba(0,0,0,0.05)",
                          minHeight: "120px",
                        }}
                      />
                      <MessageCircle
                        size={20}
                        style={{
                          position: "absolute",
                          right: "1.2rem",
                          top: "1.2rem",
                          color:
                            focusedField === "message" ? "#8b5cf6" : "#999",
                          transition: "color 0.3s ease",
                        }}
                      />
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div style={{ textAlign: "center", marginTop: "3rem" }}>
                    <button
                      type="submit"
                      onClick={handleSubmit}
                      disabled={loading}
                      style={{
                        background: loading
                          ? "#cccccc"
                          : "linear-gradient(135deg, #7EBFE9 0%, #EA9B56 100%)",
                        color: "white",
                        border: "none",
                        borderRadius: "20px",
                        padding: "1.5rem 4rem",
                        fontSize: "1.3rem",
                        fontWeight: "bold",
                        cursor: loading ? "not-allowed" : "pointer",
                        transition: "all 0.4s ease",
                        fontFamily: "'Tajawal', sans-serif",
                        boxShadow: loading
                          ? "none"
                          : "0 15px 35px rgba(126,191,233,0.3)",
                        transform: loading
                          ? "none"
                          : focusedField === "submit"
                          ? "translateY(-3px) scale(1.02)"
                          : "translateY(0) scale(1)",
                        opacity: loading ? 0.7 : 1,
                        minWidth: "200px",
                        position: "relative",
                        overflow: "hidden",
                      }}
                      onMouseEnter={() => !loading && setFocusedField("submit")}
                      onMouseLeave={() => setFocusedField("")}
                    >
                      {/* Button Shine Effect */}
                      {!loading && (
                        <div
                          style={{
                            position: "absolute",
                            top: 0,
                            left: "-100%",
                            width: "100%",
                            height: "100%",
                            background:
                              "linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)",
                            transform:
                              focusedField === "submit"
                                ? "translateX(200%)"
                                : "translateX(-100%)",
                            transition: "transform 0.6s ease",
                          }}
                        ></div>
                      )}

                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {loading ? (
                          <>
                            <div
                              style={{
                                width: "24px",
                                height: "24px",
                                border: "3px solid transparent",
                                borderTop: "3px solid white",
                                borderRadius: "50%",
                                animation: "spin 1s linear infinite",
                                marginLeft: "0.8rem",
                              }}
                            ></div>
                            جاري الإرسال...
                          </>
                        ) : (
                          <>
                            <Send
                              size={26}
                              style={{
                                marginLeft: "0.8rem",
                                transform:
                                  focusedField === "submit"
                                    ? "translateX(3px)"
                                    : "translateX(0)",
                                transition: "transform 0.3s ease",
                              }}
                            />
                            تسجيل
                          </>
                        )}
                      </div>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Footer */}
        <footer
          style={{
            background: "rgba(255,255,255,0.97)",
            backdropFilter: "blur(16px)",
            marginTop: "4rem",
            borderTop: "1px solid rgba(255,255,255,0.2)",
            boxShadow: "0 0 32px 0 rgba(126,191,233,0.12)",
            position: "relative",
          }}
        >
          <div className="container">
            <div className="row justify-content-center">
              <div className="col-12 col-lg-10">
                <div
                  style={{
                    padding: "2.5rem 2rem 2rem",
                    textAlign: "center",
                    color: "#555",
                    fontFamily: "'Tajawal', sans-serif",
                    display: "flex",
                    flexDirection: "column",
                    gap: "1.8rem",
                  }}
                >
                  {/* Title + logo */}
                  <div
                    className="footer-title-row"
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      gap: "1.2rem",
                    }}
                  >
                    <div
                      style={{
                        background:
                          "linear-gradient(135deg, #7EBFE9 0%, #EA9B56 100%)",
                        borderRadius: "15px",
                        padding: "0.8rem",
                        display: "flex",
                        alignItems: "center",
                        boxShadow: "0 8px 20px rgba(126,191,233,0.14)",
                      }}
                    >
                      <Users size={28} style={{ color: "white" }} />
                    </div>
                    <span
                      style={{
                        fontSize: "2rem",
                        color: "#EA9B56",
                        fontWeight: "bold",
                      }}
                    >
                      متاجر الحي
                    </span>
                  </div>

                  {/* Info Blocks */}
                  <div
                    className="footer-blocks-responsive"
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "2rem",
                      justifyContent: "center",
                      alignItems: "center",
                      fontSize: "1rem",
                      color: "#333",
                      lineHeight: "1.8",
                      maxWidth: "800px",
                      margin: "0 auto",
                    }}
                  >
                    {/* المركز السعودي للأعمال + اللوجو */}
                    <div
                      className="footer-saudi-block"
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.7rem",
                        minWidth: "220px",
                        justifyContent: "center",
                      }}
                    >
                      <div className="footer-logo-saudi">
                        <img
                          src={saudiLogo}
                          alt="شعار المركز السعودي للأعمال"
                          style={{
                            height: "50px",
                            marginRight: "0.6rem",
                            background: "#fff",
                            borderRadius: "10px",
                            boxShadow: "0 2px 10px rgba(0,0,0,0.07)",
                            padding: "0.15rem 0.5rem",
                          }}
                        />
                      </div>
                      <span>المركز السعودي للأعمال:</span>
                      <b>0000144419</b>
                    </div>

                    {/* مؤسسة متاجركم */}
                    <div
                      style={{
                        minWidth: "210px",
                        fontWeight: "bold",
                        fontSize: "1.07rem",
                      }}
                    >
                      مؤسسة متاجركم الذكية - س.ت 4030519889
                    </div>

                    {/* الهاتف */}
                    <div
                      ref={contactRef}
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: "0.5rem",
                        fontSize: "1.12rem",
                        justifyContent: "center",
                        minWidth: "230px",
                        direction: "rtl",
                        position: "relative",
                      }}
                    >
                      <Phone
                        size={18}
                        style={{ color: "#EA9B56", marginLeft: "0.5rem" }}
                      />
                      <span>رقم الجوال والواتساب:</span>

                      {/* الرقم نفسه */}
                      <button
                        onClick={() => setShowContactIcons(!showContactIcons)}
                        style={{
                          fontWeight: "bold",
                          color: "#7EBFE9",
                          background: "none",
                          border: "none",
                          outline: "none",
                          fontSize: "1.16rem",
                          cursor: "pointer",
                          padding: "0.3rem 0.9rem",
                          borderRadius: "12px",
                          transition:
                            "background 0.19s, color 0.19s, box-shadow 0.19s",
                          position: "relative",
                          boxShadow: showContactIcons
                            ? "0 4px 18px rgba(126,191,233,0.13)"
                            : "none",
                          backgroundColor: showContactIcons
                            ? "#f6fbfe"
                            : "transparent",
                        }}
                        title="اضغط لإظهار خيارات التواصل"
                      >
                        0561191345
                      </button>

                      {showContactIcons && (
                        <div
                          style={{
                            position: "absolute",
                            right: "105%", // شمال الزر
                            top: "50%",
                            transform: "translateY(-50%)",
                            display: "flex",
                            flexDirection: "column",
                            gap: "1rem",
                            alignItems: "center",
                            background: "#fff",
                            border: "1.5px solid #E3F0F8",
                            boxShadow: "0 10px 24px 0 rgba(126,191,233,0.18)",
                            borderRadius: "16px",
                            padding: "0.6rem 0.85rem",
                            zIndex: 100,
                            minWidth: "52px",
                            animation: "fadeinIcons 0.22s",
                          }}
                        >
                          {/* واتساب */}
                          <a
                            href="https://wa.me/966561191345"
                            target="_blank"
                            rel="noopener noreferrer"
                            style={{
                              background: "#25D366",
                              borderRadius: "50%",
                              padding: "0.52rem",
                              display: "flex",
                              alignItems: "center",
                              boxShadow: "0 2px 12px 0 rgba(37,211,102,0.16)",
                              transition: "transform 0.18s, background 0.18s",
                              fontSize: "1.4rem",
                            }}
                            title="واتساب"
                            onMouseOver={(e) =>
                              (e.currentTarget.style.transform = "scale(1.13)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                          >
                            <FaWhatsapp color="#fff" size={23} />
                          </a>
                          {/* اتصال */}
                          <a
                            href="tel:0561191345"
                            style={{
                              background:
                                "linear-gradient(135deg, #7EBFE9 0%, #EA9B56 100%)",
                              borderRadius: "50%",
                              padding: "0.52rem",
                              display: "flex",
                              alignItems: "center",
                              boxShadow: "0 2px 12px 0 rgba(126,191,233,0.15)",
                              transition: "transform 0.18s, background 0.18s",
                              fontSize: "1.4rem",
                            }}
                            title="اتصال"
                            onMouseOver={(e) =>
                              (e.currentTarget.style.transform = "scale(1.13)")
                            }
                            onMouseOut={(e) =>
                              (e.currentTarget.style.transform = "scale(1)")
                            }
                          >
                            <Phone color="#fff" size={22} />
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>

      {/* CSS Animations & Styles */}
      <style>{`
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
        
        * {
          font-family: 'Tajawal', sans-serif !important;
        }
        
        body {
          font-family: 'Tajawal', sans-serif !important;
          margin: 0;
          padding: 0;
        }
        
        input, textarea, button, label {
          font-family: 'Tajawal', sans-serif !important;
        }
        
        .container-fluid {
          padding-right: 0;
          padding-left: 0;
        }
        
        /* Smooth scrolling */
        html {
          scroll-behavior: smooth;
        }
        
        /* Custom scrollbar */
        ::-webkit-scrollbar {
          width: 8px;
        }
        
        ::-webkit-scrollbar-track {
          background: rgba(0,0,0,0.1);
        }
        
        ::-webkit-scrollbar-thumb {
          background: linear-gradient(135deg, #7EBFE9 0%, #EA9B56 100%);
          border-radius: 10px;
        }
        
        ::-webkit-scrollbar-thumb:hover {
          background: linear-gradient(135deg, #60a5fa 0%, #f97316 100%);
        }

        @keyframes fadeinIcons {
          0% { opacity: 0; transform: translateY(-10px) scale(0.93);}
          100% { opacity: 1; transform: translateY(0) scale(1);}
        }
  
        @media (max-width: 600px) {
          .footer-blocks-responsive {
            flex-direction: column !important;
            gap: 1.1rem !important;
            align-items: stretch !important;
            max-width: 98vw !important;
            margin: 0 auto !important;
            font-size: 0.97rem !important;
          }
          .footer-blocks-responsive > div {
            min-width: 0 !important;
            width: 100% !important;
            justify-content: flex-start !important;
            text-align: right !important;
            margin: 0 auto !important;
            padding: 0.7rem 0.5rem !important;
          }
          .footer-blocks-responsive > div .footer-logo-saudi {
            margin-left: 0.8rem !important;
            margin-right: 0 !important;
          }
          .footer-title-row {
            flex-direction: column !important;
            gap: 0.7rem !important;
          }
        }

        @media (max-width: 600px) {
          .footer-saudi-block {
            flex-direction: column !important;
            gap: 0.4rem !important;
            align-items: flex-start !important;
            text-align: right !important;
          }
          .footer-saudi-block .footer-logo-saudi {
            margin-left: 0 !important;
            margin-bottom: 0.3rem !important;
            margin-right: 0 !important;
          }
        }

      `}</style>
    </div>
  );
}
